import type { SxProps, Theme } from '@mui/material';
import { Link as MuiLink, LinkProps as MuiLinkProps } from '@mui/material';
import { useMemo, type ReactNode } from 'react';
import { Link as RouterLink, useHistory } from 'react-router-dom';
import GaTracker from '../../utils/GaTracker/GaTracker';

export default function Link({
  to,
  children,
  sx,
  external: _external,
  fullWidth,
  onClick,
  action,
  target,
}: {
  to: string;
  children: ReactNode;
  sx?: SxProps<Theme>;
  external?: boolean;
  fullWidth?: boolean;
  onClick?: () => void;
  action: string;
  target?: MuiLinkProps['target'];
}) {
  const history = useHistory();
  const external = _external || to.startsWith('http://') || to.startsWith('https://');

  const gaAction = useMemo(() => {
    const pathname = history?.location?.pathname;

    if (external) {
      return `convert-to-${action}`;
    } else if (typeof pathname === 'string' && typeof to === 'string') {
      return `navigate-from${
        pathname === '/' ? '-home' : pathname.replace('/', '-')
      }-to${to.replace('/', '-')}`;
    } else if (typeof pathname === 'string') {
      return `navigate-from${pathname === '/' ? '-home' : pathname.replace('/', '-')}`;
    } else if (typeof to === 'string') {
      return `navigate-to${to.replace('/', '-')}`;
    } else {
      return `navigate-${action}`;
    }
  }, [to, history?.location?.pathname, action, external]);

  return (
    <MuiLink
      component={RouterLink}
      onClick={() => {
        GaTracker.trackEvent({
          category: 'Link',
          action: gaAction,
          label: to,
        });
        onClick?.();
      }}
      {...(external
        ? {
            rel: 'noopener noreferrer',
            target: target || '_blank',
            to: { pathname: to },
          }
        : {
            to: to,
          })}
      sx={{
        fontWeight: 'bold',
        textDecoration: 'unset',
        color: (theme) => theme.palette.blue[700],
        width: fullWidth ? '100%' : 'auto',
        ...(sx || {}),
      }}
    >
      {children}
    </MuiLink>
  );
}
